import React, { useState, useEffect, useCallback } from 'react';

const REDIRECT_URI = (window.location.origin + '/api/email/oauth/callback');

const O365_STEPS = [
  {
    title: 'Register an application',
    body: (
      <>
        Go to the{' '}
        <a
          href="https://entra.microsoft.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Microsoft Entra admin center
        </a>{' '}
        and navigate to <strong>Identity → Applications → App registrations → New registration</strong>.
      </>
    ),
  },
  {
    title: 'Fill in application details',
    body: (
      <ul className="list-disc list-inside space-y-1 mt-1">
        <li>
          <strong>Name:</strong> [Company] MC Mail
        </li>
        <li>
          <strong>Supported account types:</strong> Accounts in this organizational directory only
        </li>
        <li>
          <strong>Redirect URI:</strong> Web →{' '}
          <code className="text-sm bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{REDIRECT_URI}</code>
        </li>
      </ul>
    ),
  },
  {
    title: 'Copy Application (client) ID and Directory (tenant) ID',
    body: (
      <p>
        On the app <strong>Overview</strong> page, note the <strong>Application (client) ID</strong> and{' '}
        <strong>Directory (tenant) ID</strong> — you'll paste these below.
      </p>
    ),
  },
  {
    title: 'Create a client secret',
    body: (
      <p>
        Go to <strong>Certificates &amp; secrets → New client secret</strong>, add a description and expiry, then copy
        the <strong>Value</strong> (not the Secret ID).
      </p>
    ),
  },
  {
    title: 'Grant Microsoft Graph permissions',
    body: (
      <ul className="list-disc list-inside space-y-1 mt-1">
        <li>
          <strong>API permissions → Add a permission → Microsoft Graph → Application permissions</strong>
        </li>
        <li>Add: <code>Mail.Send</code>, <code>Mail.Read</code>, <code>Mail.ReadBasic</code></li>
        <li>
          Click <strong>Grant admin consent</strong> for your tenant
        </li>
      </ul>
    ),
  },
];

const GOOGLE_STEPS = [
  {
    title: 'Create or select a project',
    body: (
      <>
        Go to the{' '}
        <a
          href="https://console.cloud.google.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Google Cloud Console
        </a>{' '}
        and create or select a project for this integration.
      </>
    ),
  },
  {
    title: 'Enable the Gmail API',
    body: (
      <p>
        Navigate to <strong>APIs &amp; Services → Library</strong>, search for <strong>Gmail API</strong>, and click{' '}
        <strong>Enable</strong>.
      </p>
    ),
  },
  {
    title: 'Configure the OAuth consent screen',
    body: (
      <ul className="list-disc list-inside space-y-1 mt-1">
        <li>
          Go to <strong>APIs &amp; Services → OAuth consent screen</strong>
        </li>
        <li>
          Choose <strong>Internal</strong> (for Google Workspace) and fill in the required app information
        </li>
        <li>
          Add scopes: <code>gmail.send</code> and <code>gmail.readonly</code>
        </li>
      </ul>
    ),
  },
  {
    title: 'Create OAuth credentials',
    body: (
      <ul className="list-disc list-inside space-y-1 mt-1">
        <li>
          Go to <strong>APIs &amp; Services → Credentials → Create credentials → OAuth client ID</strong>
        </li>
        <li>
          Choose <strong>Web application</strong>
        </li>
        <li>
          Add <strong>Authorized redirect URI</strong>:{' '}
          <code className="text-sm bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{REDIRECT_URI}</code>
        </li>
      </ul>
    ),
  },
  {
    title: 'Copy Client ID and Client Secret',
    body: <p>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> shown after creation.</p>,
  },
];

function StatusBadge({ status }) {
  const map = {
    connected: { label: 'Connected', cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
    disconnected: { label: 'Not connected', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
    error: { label: 'Error', cls: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  };
  const m = map[status] || map.disconnected;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <span
        className={`mr-1.5 h-2 w-2 rounded-full ${
          status === 'connected'
            ? 'bg-green-500'
            : status === 'error'
            ? 'bg-red-500'
            : 'bg-gray-400'
        }`}
      />
      {m.label}
    </span>
  );
}

function StepList({ steps }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-xs font-semibold">
            {i + 1}
          </span>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-gray-100">{s.title}</p>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">{s.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function EmailIntegration() {
  const [status, setStatus] = useState(null); // { state, provider?, email?, error? }
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState(null);
  const [tab, setTab] = useState('o365');

  const [form, setForm] = useState({
    clientId: '',
    clientSecret: '',
    tenantId: '',
    fromEmail: '',
    fromName: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await fetch('/api/email/oauth/status', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to load status');
      // API returns { status: 'connected' } — normalize to { state: 'connected' } for frontend
      setStatus({ ...data, state: data.status || 'disconnected' });
      if (data.provider === 'google') setTab('google');
    } catch (err) {
      setStatusError(err.message);
      setStatus({ state: 'error', error: err.message });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        provider: tab === 'o365' ? 'o365' : 'google',
        client_id: form.clientId.trim(),
        client_secret: form.clientSecret.trim(),
        from_email: form.fromEmail.trim(),
        from_name: form.fromName.trim(),
      };
      if (tab === 'o365') payload.tenant_id = form.tenantId.trim();

      const res = await fetch('/api/email/oauth/configure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to save configuration');

      // Start OAuth flow
      window.location.href = '/api/email/oauth/start';
    } catch (err) {
      setFormError(err.message);
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your email integration? You will need to reconfigure to reconnect.')) return;
    setFormError(null);
    try {
      const res = await fetch('/api/email/oauth/disconnect', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Failed to disconnect');
      setStatus({ state: 'disconnected' });
      setForm({ clientId: '', clientSecret: '', tenantId: '', fromEmail: '', fromName: '' });
    } catch (err) {
      setFormError(err.message);
    }
  };

  const isO365 = tab === 'o365';
  const isConnected = status?.state === 'connected';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Integration</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Connect your email account to send sales emails and track replies. We use OAuth 2.0 — we never store your
            password.
          </p>
        </div>

        {/* Status banner */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {statusLoading ? (
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading status…</span>
              ) : (
                <>
                  <StatusBadge status={status?.state || 'disconnected'} />
                  {isConnected && (
                    <div className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {status?.provider === 'google' ? 'Google Workspace' : 'Microsoft 365'}
                      </span>
                      {status?.email && (
                        <span className="text-gray-500 dark:text-gray-400"> · {status.email}</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {isConnected && (
              <button
                onClick={handleDisconnect}
                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 dark:bg-gray-800 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
              >
                Disconnect
              </button>
            )}
          </div>

          {/* Error detail */}
          {status?.state === 'error' && status?.error && (
            <div className="mt-3 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
              <strong className="font-medium">Error:</strong> {status.error}
            </div>
          )}
          {statusError && (
            <div className="mt-3 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
              {statusError}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex gap-6" aria-label="Tabs">
              <button
                onClick={() => setTab('o365')}
                className={`whitespace-nowrap py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  isO365
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Microsoft 365
              </button>
              <button
                onClick={() => setTab('google')}
                className={`whitespace-nowrap py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  !isO365
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                Google Workspace
              </button>
            </nav>
          </div>
        </div>

        {/* Tab content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Instructions */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {isO365 ? 'Set up your Microsoft 365 app' : 'Set up your Google Workspace app'}
            </h2>
            <StepList steps={isO365 ? O365_STEPS : GOOGLE_STEPS} />
            <div className="mt-6 rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 text-sm text-blue-700 dark:text-blue-300">
              <strong>Redirect URI:</strong>{' '}
              <code className="text-sm bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded">{REDIRECT_URI}</code>
            </div>
          </div>

          {/* Configuration form */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {isO365 ? 'Microsoft 365 credentials' : 'Google Workspace credentials'}
            </h2>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label
                  htmlFor="clientId"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Client ID
                </label>
                <input
                  id="clientId"
                  name="clientId"
                  type="text"
                  required
                  value={form.clientId}
                  onChange={handleField}
                  placeholder={isO365 ? 'Application (client) ID' : 'Google Client ID'}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 sm:text-sm"
                />
              </div>

              {isO365 && (
                <div>
                  <label
                    htmlFor="tenantId"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Tenant ID
                  </label>
                  <input
                    id="tenantId"
                    name="tenantId"
                    type="text"
                    required
                    value={form.tenantId}
                    onChange={handleField}
                    placeholder="Directory (tenant) ID"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 sm:text-sm"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="clientSecret"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Client Secret
                </label>
                <input
                  id="clientSecret"
                  name="clientSecret"
                  type="password"
                  required
                  value={form.clientSecret}
                  onChange={handleField}
                  placeholder={isO365 ? 'Client secret value' : 'Google Client Secret'}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 sm:text-sm"
                />
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Sender identity</h3>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fromEmail"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      From Email
                    </label>
                    <input
                      id="fromEmail"
                      name="fromEmail"
                      type="email"
                      required
                      value={form.fromEmail}
                      onChange={handleField}
                      placeholder="sales@company.com"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="fromName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      From Name
                    </label>
                    <input
                      id="fromName"
                      name="fromName"
                      type="text"
                      required
                      value={form.fromName}
                      onChange={handleField}
                      placeholder="Company Sales"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {formError && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
                  {formError}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-800"
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Saving…
                    </>
                  ) : (
                    'Save & Connect'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}